import { Component, input, computed } from '@angular/core';

interface BadgeStatusDefinition {
  className: string;
  icon: string;
}

const BADGE_STATUS_DEFINITIONS: Record<string, BadgeStatusDefinition> = {
  // Tipos genéricos
  PRIMARY: { className: 'badge-primary', icon: 'bi-circle-fill' },
  SECONDARY: { className: 'badge-secondary', icon: 'bi-dash-circle' },
  SUCCESS: { className: 'badge-success', icon: 'bi-check-circle-fill' },
  ERROR: { className: 'badge-error', icon: 'bi-x-circle-fill' },
  DANGER: { className: 'badge-error', icon: 'bi-x-circle-fill' },
  WARNING: { className: 'badge-warning', icon: 'bi-exclamation-triangle-fill' },
  INFO: { className: 'badge-info', icon: 'bi-info-circle-fill' },
  ADMIN: { className: 'badge-admin', icon: 'bi-shield-lock-fill' },
  PURPLE: { className: 'badge-purple', icon: 'bi-polygon-fill' },
  TEAL: { className: 'badge-teal', icon: 'bi-people-fill' },
  INDIGO: { className: 'badge-indigo', icon: 'bi-diagram-3-fill' },
  YELLOW: { className: 'badge-yellow', icon: 'bi-star-fill' },
  MUTED: { className: 'badge-muted', icon: 'bi-exclamation-circle' },

  // SGI — status de entidade
  ATIVO: { className: 'badge-success', icon: 'bi-check-circle-fill' },
  INATIVO: { className: 'badge-error', icon: 'bi-x-circle-fill' },
  DISPONIVEL: { className: 'badge-success', icon: 'bi-box-arrow-up-right' },
  INDISPONIVEL: { className: 'badge-error', icon: 'bi-slash-circle' },
  EXPIRADO: { className: 'badge-error', icon: 'bi-hourglass-bottom' },

  // SGI — papéis de usuário
  ADMINISTRADOR: { className: 'badge-admin', icon: 'bi-shield-lock-fill' },
  COLABORADOR: { className: 'badge-info', icon: 'bi-person-fill' },
  USUARIO: { className: 'badge-info', icon: 'bi-person' },

  // Agendamentos / atendimentos
  AGENDADO: { className: 'badge-info', icon: 'bi-calendar-check' },
  CONFIRMADO: { className: 'badge-success', icon: 'bi-check2-circle' },
  EM_ATENDIMENTO: { className: 'badge-warning', icon: 'bi-activity' },
  REALIZADO: { className: 'badge-primary', icon: 'bi-clipboard2-check' },
  CONCLUIDO: { className: 'badge-primary', icon: 'bi-clipboard2-check' },
  CONCLUIDA: { className: 'badge-primary', icon: 'bi-clipboard2-check' },
  CANCELADO: { className: 'badge-error', icon: 'bi-x-octagon-fill' },
  CRIADO: { className: 'badge-info', icon: 'bi-plus-circle' },
  PENDENTE: { className: 'badge-warning', icon: 'bi-clock-fill' },
  FINALIZADO: { className: 'badge-success', icon: 'bi-check-circle-fill' },
  FINALIZADA: { className: 'badge-success', icon: 'bi-check-circle-fill' },

  // Evoluções / processos
  EM_ANDAMENTO: { className: 'badge-warning', icon: 'bi-arrow-repeat' },
  RASCUNHO: { className: 'badge-secondary', icon: 'bi-pencil-square' },
  EM_REVISAO: { className: 'badge-info', icon: 'bi-search' },
  A_SER_PREENCHIDO: { className: 'badge-warning', icon: 'bi-ui-checks-grid' },
  ERRO_VALIDACAO: { className: 'badge-error', icon: 'bi-exclamation-octagon' },
  PREENCHIDO: { className: 'badge-success', icon: 'bi-check2-square' },
  PROCESSADA: { className: 'badge-success', icon: 'bi-check-all' },
  REJEITADA: { className: 'badge-error', icon: 'bi-hand-thumbs-down-fill' },
  EM_PROCESSAMENTO: { className: 'badge-warning', icon: 'bi-arrow-repeat' },
};

const GENERIC_BADGE_TYPES = new Set([
  'PRIMARY',
  'SECONDARY',
  'SUCCESS',
  'ERROR',
  'DANGER',
  'WARNING',
  'INFO',
  'ADMIN',
  'PURPLE',
  'TEAL',
  'INDIGO',
  'YELLOW',
  'MUTED',
]);

const LABEL_TO_STATUS_KEY: Record<string, string> = {
  AGENDADO: 'AGENDADO',
  CONFIRMADO: 'CONFIRMADO',
  EM_ATENDIMENTO: 'EM_ATENDIMENTO',
  REALIZADO: 'CONCLUIDO',
  CONCLUIDO: 'CONCLUIDO',
  CONCLUIDA: 'CONCLUIDA',
  CANCELADO: 'CANCELADO',
  CRIADO: 'CRIADO',
  PENDENTE: 'PENDENTE',
  FINALIZADO: 'FINALIZADO',
  FINALIZADA: 'FINALIZADA',
  EM_ANDAMENTO: 'EM_ANDAMENTO',
  RASCUNHO: 'RASCUNHO',
  EM_REVISAO: 'EM_REVISAO',
  A_SER_PREENCHIDO: 'A_SER_PREENCHIDO',
  ERRO_VALIDACAO: 'ERRO_VALIDACAO',
  PREENCHIDO: 'PREENCHIDO',
  PROCESSADA: 'PROCESSADA',
  REJEITADA: 'REJEITADA',
  EM_PROCESSAMENTO: 'EM_PROCESSAMENTO',
  ATIVO: 'ATIVO',
  ATIVA: 'ATIVO',
  INATIVO: 'INATIVO',
  INATIVA: 'INATIVO',
  DISPONIVEL: 'DISPONIVEL',
  INDISPONIVEL: 'INDISPONIVEL',
  EXPIRADO: 'EXPIRADO',
  ADMINISTRADOR: 'ADMINISTRADOR',
  COLABORADOR: 'COLABORADOR',
  USUARIO: 'USUARIO',
};

const DEFAULT_BADGE_STATUS: BadgeStatusDefinition = {
  className: 'badge-primary',
  icon: 'bi-tag-fill',
};

@Component({
  selector: 'app-badge',
  standalone: true,
  template: `
    <span
      class="badge"
      title="{{ title() }}"
      [class]="badgeClassToShow()"
      [class.is-live-status]="isLiveStatus()"
      [class.is-processing-status]="isProcessingStatus()"
      [attr.aria-label]="'Status: ' + label()"
    >
      @if (isLiveStatus()) {
        <span class="badge-live-dot" aria-hidden="true"></span>
      }
      @if (hasIconToShow()) {
        <i class="bi" [class]="iconToShow()" aria-hidden="true"></i>
      }
      @if(label()){
        <span class="badge-text">{{ label() }}</span>
      }
    </span>
  `,
  styles: `
    @use 'sass:map';
    @use 'variables' as *;
    @use 'mixins' as *;

    :host{
      @include flex(row, flex-start, center, nowrap, $space-1);
    }

    .badge {
      @include text-truncate(1);
      @include flex(row, flex-start, center, nowrap, $space-1);
      display: inline-flex;
      position: relative;
      padding: 0.2rem 0.65rem;
      border-radius: $radius-full;
      font-size: $font-size-xxs;
      font-weight: $font-weight-medium;
      text-transform: none;
      letter-spacing: 0.01em;
      border: 1px solid transparent;
      white-space: nowrap;
      transition: $transition-fast;
      line-height: 1.25;

      i { 
        font-size: 0.65rem;
        margin-right: 0.15rem;
        flex-shrink: 0;
      }

      .badge-live-dot {
        width: 7px;
        height: 7px;
        border-radius: 50%;
        background: currentColor;
        margin-right: 0.15rem;
        flex-shrink: 0;
        box-shadow: 0 0 0 0 rgba($color-warning, 0.55);
      }

      .badge-text {
        font-weight: $font-weight-medium;
      }

      @mixin badge-tone($color) {
        background: rgba($color, 0.1);
        color: $color;
        border-color: rgba($color, 0.08);
      }

      &.badge-primary { @include badge-tone($color-primary); }
      &.badge-success { @include badge-tone(map.get($colors, 'success', 'base')); }
      &.badge-error { @include badge-tone(map.get($colors, 'error', 'base')); }
      &.badge-warning { @include badge-tone(map.get($colors, 'warning', 'base')); }
      &.badge-info { @include badge-tone(map.get($colors, 'info', 'base')); }
      &.badge-secondary { @include badge-tone(map.get($colors, 'gray', '500')); }
      &.badge-motoboy { @include badge-tone(map.get($colors, 'info', 'base')); }
      &.badge-sedex { @include badge-tone(map.get($colors, 'yellow', 'base')); }
      &.badge-purple { @include badge-tone(#6b21a8); }
      &.badge-indigo { @include badge-tone(#3730a3); }
      &.badge-teal { @include badge-tone(#0d9488); }
      &.badge-admin { @include badge-tone($color-success); }
      &.badge-yellow { @include badge-tone(#e6a22e); }
      &.badge-muted { @include badge-tone(map.get($colors, 'gray', '500')); }

      &.is-live-status {
        .badge-live-dot {
          animation: liveDotPulse 1.5s ease-out infinite;
        }
      }

      &.is-processing-status i {
        animation: softSpin 2.2s linear infinite;
      }
    }

    @keyframes liveDotPulse {
      0% {
        box-shadow: 0 0 0 0 rgba($color-warning, 0.5);
      }
      75% {
        box-shadow: 0 0 0 7px rgba($color-warning, 0);
      }
      100% {
        box-shadow: 0 0 0 0 rgba($color-warning, 0);
      }
    }

    @keyframes softSpin {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }

    @media (prefers-reduced-motion: reduce) {
      .badge.is-live-status .badge-live-dot,
      .badge.is-processing-status i {
        animation: none;
      }
    }
  `,
})
export class BadgeComponent {
  icon = input<string>('');
  label = input<string>('');
  title = input<string>('');
  type = input<string>('primary');

  private readonly normalizedLabel = computed(() =>
    String(this.label() || '')
      .toUpperCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim(),
  );

  private readonly normalizedType = computed(() =>
    String(this.type() || '')
      .toUpperCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim(),
  );

  private readonly resolvedStatusKey = computed(() => {
    const type = this.normalizedType();
    const labelKey = this.normalizedLabel().replace(/\s+/g, '_');

    if (type && !GENERIC_BADGE_TYPES.has(type)) {
      return type;
    }

    return LABEL_TO_STATUS_KEY[labelKey] ?? (type || 'PRIMARY');
  });

  private readonly statusDefinition = computed(
    () => BADGE_STATUS_DEFINITIONS[this.resolvedStatusKey()] ?? DEFAULT_BADGE_STATUS,
  );

  readonly badgeClassToShow = computed(() => this.statusDefinition().className);
  readonly hasIconToShow = computed(() => String(this.iconToShow() || '').trim().length > 0);

  readonly iconToShow = computed(() => {
    const explicitIcon = String(this.icon() || '').trim();
    if (explicitIcon) {
      return explicitIcon;
    }
    return this.statusDefinition().icon;
  });

  readonly isLiveStatus = computed(() => {
    const key = this.resolvedStatusKey();
    const rawLabel = this.normalizedLabel();

    return (
      key === 'EM_ATENDIMENTO' ||
      key === 'EM_ANDAMENTO' ||
      key === 'EM_PROCESSAMENTO' ||
      rawLabel.includes('EM ATENDIMENTO') ||
      rawLabel.includes('EM ANDAMENTO') ||
      rawLabel.includes('PROCESSANDO') ||
      rawLabel.includes('EM PROCESSAMENTO')
    );
  });

  readonly isProcessingStatus = computed(() => {
    const key = this.resolvedStatusKey();
    const rawLabel = this.normalizedLabel();

    return (
      key === 'EM_PROCESSAMENTO' ||
      rawLabel.includes('PROCESSANDO') ||
      rawLabel.includes('EM PROCESSAMENTO')
    );
  });
}
